import { useState, useEffect, useRef, useCallback, FormEvent } from 'react';
import { KeySquare, ArrowLeft } from 'lucide-react';
import { SendOtpResponse } from '../../services/authService';
import { ButtonWithIcon } from '../ui/button';
import { OtpInput } from './OtpInput';
import { useResendTimer } from '../../hooks/useResendTimer';

type OtpStep = 'identifier' | 'verify';

interface OtpLoginStepProps {
  programCode?: string;
  onSendOtp: (identifier: string, programCode?: string) => Promise<{ success: boolean; message: string; data?: SendOtpResponse }>;
  onOtpLogin: (identifier: string, otpCode: string, programCode?: string, rememberMe?: boolean) => Promise<void>;
  onBack?: () => void;
  error?: string;
}

export default function OtpLoginStep({ programCode, onSendOtp, onOtpLogin, onBack, error: externalError }: OtpLoginStepProps) {
  const [step, setStep] = useState<OtpStep>('identifier');
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [sentData, setSentData] = useState<SendOtpResponse | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [localError, setLocalError] = useState('');
  const identifierRef = useRef<HTMLInputElement>(null);
  const resendTimer = useResendTimer(30);
  const autoSubmitRef = useRef(false);

  useEffect(() => { identifierRef.current?.focus(); }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleSend = async (e?: FormEvent) => {
    e?.preventDefault();
    if (loading) return;
    if (!identifier.trim()) { setLocalError('Enter your username or email'); return; }
    setLocalError('');
    setLoading(true);
    try {
      const result = await onSendOtp(identifier.trim(), programCode);
      if (!result.success) { setLocalError(result.message); return; }
      if (result.data) {
        setSentData(result.data);
        setSecondsLeft(result.data.expirySeconds);
      }
      setStep('verify');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer.isActive || resending) return;
    setResending(true);
    setOtp('');
    setLocalError('');
    try {
      const result = await onSendOtp(identifier.trim(), programCode);
      if (!result.success) { setLocalError(result.message); return; }
      if (result.data) {
        setSentData(result.data);
        setSecondsLeft(result.data.expirySeconds);
      }
      resendTimer.start();
    } finally {
      setResending(false);
    }
  };

  const handleVerify = useCallback(async (e?: FormEvent) => {
    e?.preventDefault();
    if (otp.length !== 6 || loading) return;
    setLocalError('');
    setLoading(true);
    try {
      await onOtpLogin(identifier.trim(), otp, programCode, rememberMe);
    } finally {
      setLoading(false);
    }
  }, [otp, loading, identifier, programCode, rememberMe, onOtpLogin]);

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (otp.length === 6 && step === 'verify' && !loading && !autoSubmitRef.current) {
      autoSubmitRef.current = true;
      handleVerify();
    }
    if (otp.length < 6) {
      autoSubmitRef.current = false;
    }
  }, [otp, step, loading, handleVerify]);

  const displayError = externalError || localError;
  const isExpired = secondsLeft <= 0 && step === 'verify';

  if (step === 'identifier') {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-soft dark:bg-primary/10">
            <KeySquare className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-base font-semibold text-foreground">Log in with OTP</h3>
          <p className="text-sm text-muted-foreground">Enter your username or email to receive a one-time code</p>
        </div>

        <form onSubmit={handleSend} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-foreground">Username or email</label>
            <input
              ref={identifierRef}
              type="text"
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              placeholder="Enter username or email"
              className="w-full h-11 px-3 text-base rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={loading}
            />
          </div>

          {displayError && (
            <p className="text-sm text-destructive text-center" role="alert">{displayError}</p>
          )}

          <ButtonWithIcon
            type="submit"
            icon={KeySquare}
            disabled={loading || !identifier.trim()}
            className="w-full"
          >
            {loading ? 'Sending...' : 'Send verification code'}
          </ButtonWithIcon>
        </form>

        {onBack && (
          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to password login
            </button>
          </div>
        )}
      </div>
    );
  }

  // OTP verify step
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-soft dark:bg-primary/10">
          <KeySquare className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-base font-semibold text-foreground">Enter verification code</h3>
        {sentData && (
          <p className="text-sm text-muted-foreground">
            Code sent via <span className="font-medium">{sentData.channel}</span> to{' '}
            <span className="font-medium text-foreground">{sentData.maskedContact}</span>
          </p>
        )}
      </div>

      <form onSubmit={handleVerify} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-foreground">Verification code</label>
          <OtpInput
            value={otp}
            onChange={setOtp}
            error={!!displayError}
            disabled={loading || isExpired}
          />
          <p className={`text-xs text-right ${isExpired ? 'text-destructive' : 'text-muted-foreground'}`}>
            {isExpired ? 'Code expired' : secondsLeft > 0 ? `Expires in ${formatCountdown(secondsLeft)}` : ''}
          </p>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={e => setRememberMe(e.target.checked)}
            className="w-4 h-4 rounded border-border text-primary"
          />
          <span className="text-sm text-muted-foreground">Remember me</span>
        </label>

        {displayError && (
          <p className="text-sm text-destructive text-center" role="alert">{displayError}</p>
        )}

        <ButtonWithIcon
          type="submit"
          icon={KeySquare}
          disabled={loading || otp.length !== 6 || isExpired}
          className="w-full"
        >
          {loading ? 'Verifying...' : 'Verify & log in'}
        </ButtonWithIcon>
      </form>

      {/* Standard bottom actions */}
      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          Didn't receive code?{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={resending || resendTimer.isActive}
            className="text-primary hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendTimer.isActive ? `Resend in ${resendTimer.seconds}s` : resending ? 'Sending...' : 'Resend'}
          </button>
        </p>
        <button
          type="button"
          onClick={() => { setStep('identifier'); setOtp(''); setLocalError(''); }}
          className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" />
          Change identifier
        </button>
      </div>
    </div>
  );
}
